import { Box, FormikTextInput } from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import get from 'lodash/get';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

import { MonthNamesShort } from 'types';

interface TwelveMonthsNumberInputProps {
  isLoading: boolean;
  fieldName: string;
  min?: number;
  max?: number;
}

export const TwelveMonthsNumberInput = ({
  isLoading,
  fieldName,
  min = Number.MIN_VALUE,
  max = Number.MAX_VALUE,
}: TwelveMonthsNumberInputProps) => {
  const { isSubmitting, errors, setFieldValue, values } = useFormikContext<any>();
  const isAnythingLoadingOrSubmitting = isLoading || isSubmitting;
  const [rangeError, setRangeError] = useState<string | null>(null);

  const setValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const numericValue = Number(event.target.value);
      if (!isNaN(numericValue) && numericValue >= min && numericValue <= max) {
        setFieldValue(event.target.name, numericValue);
        setRangeError(null);
      } else {
        const oldValue = get(values, event.target.name);
        setFieldValue(event.target.name, oldValue ?? '');
        setRangeError(`Values must be between ${min} and ${max}`);
      }
    },
    [max, min, setFieldValue, values],
  );

  const fieldError = useMemo(() => {
    return get(errors, fieldName);
  }, [errors, fieldName]);

  return (
    <Box
      childGap={{ base: 'lg', desktop: 'xl' }}
      display="grid"
      onBlur={() => setRangeError(null)}
      style={{
        gridTemplateColumns: 'repeat(6,minmax(0,1fr))',
        columnGap: 'var(--size-spacing-sm)',
        rowGap: 'var(--size-spacing-sm)',
      }}
    >
      <FieldArray
        name="firstYearMonthlyProductionKwh"
        render={() =>
          MonthNamesShort.map((month: string, index: number) => (
            <Box key={index}>
              <Field
                label={month}
                name={`${fieldName}[${index}]`}
                id={month}
                component={FormikTextInput}
                isDisabled={isAnythingLoadingOrSubmitting}
                form={{ errors: [] }}
                onChange={setValue}
              />
            </Box>
          ))
        }
      />
      <Box style={{ gridColumn: '1 / -1' }}>
        {rangeError ? (
          <Box color="danger">{rangeError}</Box>
        ) : fieldError ? (
          <Box color="danger">{`${fieldError}`}</Box>
        ) : null}
      </Box>
    </Box>
  );
};
